import React from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { useCalendlyPopup } from '../hooks/useCalendlyPopup';
import { SharedLayout } from '../components/layout/SharedLayout';

const HeroSection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px 80px;
  text-align: center;
`;

const HeroContent = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const UrgencyBadge = styled.div`
  display: inline-block;
  background: rgba(255, 87, 87, 0.1);
  color: #ff5757;
  padding: 8px 16px;
  border-radius: 20px;
  font-size: 14px;
  margin-bottom: 30px;
  border: 1px solid rgba(255, 87, 87, 0.3);
  font-weight: 600;
`;

const HeroTitle = styled.h1`
  font-size: clamp(36px, 5vw, 56px);
  font-weight: 800;
  color: white;
  margin-bottom: 25px;
  line-height: 1.1;
`;

const HeroSubtitle = styled.p`
  font-size: clamp(20px, 2.5vw, 24px);
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
  line-height: 1.4;
`;

const CTAButton = styled.button`
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  color: #333;
  padding: 20px 40px;
  font-size: 18px;
  font-weight: bold;
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.3s;
  text-transform: uppercase;
  letter-spacing: 1px;
  box-shadow: 0 10px 30px rgba(255, 215, 0, 0.3);
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: 0 15px 40px rgba(255, 215, 0, 0.4);
  }
`;

const SecondaryButton = styled.a`
  display: inline-block;
  color: white;
  padding: 16px 32px;
  font-size: 16px;
  font-weight: bold;
  border: 2px solid white;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s;
  margin-left: 20px;
  
  &:hover {
    background: white;
    color: #232B3B;
  }
  
  @media (max-width: ${theme.breakpoints.mobile}) {
    display: block;
    margin: 20px auto 0;
    width: fit-content;
  }
`;

const Section = styled.section<{ bg?: string }>`
  padding: 80px 20px;
  background: ${props => props.bg || 'white'};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const SectionTitle = styled.h2`
  font-size: clamp(32px, 4vw, 44px);
  font-weight: 800;
  text-align: center;
  margin-bottom: 25px;
  color: #333;
`;

const SectionSubtitle = styled.p`
  font-size: clamp(18px, 2.5vw, 22px);
  text-align: center;
  margin-bottom: 60px;
  color: #666;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  line-height: 1.5;
`;

const ThreatGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  
  @media (max-width: 1200px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const ThreatCard = styled.div`
  background: white;
  padding: 35px;
  border-radius: 12px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border-left: 4px solid #ff5757;
  transition: transform 0.3s;
  
  &:hover {
    transform: translateY(-5px);
  }
`;

const ThreatIcon = styled.div`
  font-size: 36px;
  margin-bottom: 20px;
`;

const ThreatTitle = styled.h3`
  font-size: 22px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
`;

const ThreatText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
`;

const SolutionGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 40px;
`;

const SolutionCard = styled.div`
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  padding: 40px;
  border-radius: 16px;
  text-align: center;
  border: 2px solid #ffd700;
`;

const SolutionIcon = styled.div`
  width: 70px;
  height: 70px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 50%;
  margin: 0 auto 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 32px;
`;

const SolutionTitle = styled.h3`
  font-size: 24px;
  font-weight: bold;
  margin-bottom: 15px;
  color: #333;
`;

const SolutionText = styled.p`
  font-size: 17px;
  line-height: 1.6;
  color: #666;
`;

const TestimonialSection = styled.section`
  background: #f8f9fa;
  padding: 80px 20px;
`;

const TestimonialGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: 30px;
  max-width: 1200px;
  margin: 0 auto;
`;

const TestimonialCard = styled.div`
  background: white;
  padding: 30px;
  border-radius: 12px;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.08);
`;

const Stars = styled.div`
  color: #ffd700;
  font-size: 20px;
  margin-bottom: 15px;
`;

const TestimonialText = styled.p`
  font-size: 16px;
  line-height: 1.6;
  color: #666;
  margin-bottom: 20px;
  font-style: italic;
`;

const TestimonialAuthor = styled.div`
  font-weight: bold;
  color: #333;
`;

const ProcessSection = styled.section`
  padding: 80px 20px;
  background: white;
`;

const ProcessSteps = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  max-width: 1200px;
  margin: 0 auto;
  
  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 640px) {
    grid-template-columns: 1fr;
  }
`;

const ProcessStep = styled.div`
  text-align: center;
`;

const StepNumber = styled.div`
  width: 60px;
  height: 60px;
  background: linear-gradient(135deg, #ffd700 0%, #ffed4e 100%);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 24px;
  font-weight: bold;
  color: #333;
  margin: 0 auto 20px;
`;

const StepTitle = styled.h3`
  font-size: 20px;
  font-weight: bold;
  margin-bottom: 12px;
  color: #333;
`;

const StepText = styled.p`
  font-size: 15px;
  line-height: 1.5;
  color: #666;
`;

const CTASection = styled.section`
  background: linear-gradient(135deg, #232B3B 0%, #1a1f2e 100%);
  padding: 100px 20px;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: clamp(32px, 4vw, 48px);
  color: white;
  margin-bottom: 25px;
`;

const CTAText = styled.p`
  font-size: 22px;
  color: rgba(255, 255, 255, 0.9);
  margin-bottom: 40px;
  line-height: 1.5;
`;

export const InflationProtection: React.FC = () => {
  const { openCalendly } = useCalendlyPopup();
  
  return (
    <SharedLayout>
      <HeroSection>
        <HeroContent>
          <UrgencyBadge>INFLATION IS AT A 40-YEAR HIGH</UrgencyBadge>
          <HeroTitle>
            Your Retirement Savings Are Under Attack
          </HeroTitle>
          <HeroSubtitle>
            Every day you wait, inflation erodes your purchasing power. 
            Get expert help protecting your retirement with proven annuity strategies.
          </HeroSubtitle>
          
          <div>
            <CTAButton onClick={openCalendly}>
              GET YOUR FREE INFLATION PROTECTION ANALYSIS
            </CTAButton>
            <SecondaryButton href="tel:800-922-1894">
              CALL NOW: 800-922-1894
            </SecondaryButton>
          </div>
        </HeroContent>
      </HeroSection>

      <Section>
        <Container>
          <SectionTitle>The Hidden Threats to Your Retirement</SectionTitle>
          <SectionSubtitle>
            Most retirees don't realize how much inflation impacts their savings until it's too late
          </SectionSubtitle>
          
          <ThreatGrid>
            <ThreatCard>
              <ThreatIcon>📉</ThreatIcon>
              <ThreatTitle>Purchasing Power Loss</ThreatTitle>
              <ThreatText>
                At 3% inflation, your $100,000 will only buy $74,000 worth of goods in 10 years. 
                At current rates, it could be much worse.
              </ThreatText>
            </ThreatCard>
            
            <ThreatCard>
              <ThreatIcon>🏥</ThreatIcon>
              <ThreatTitle>Healthcare Cost Explosion</ThreatTitle>
              <ThreatText>
                Medical costs rise faster than general inflation. The average couple needs 
                $315,000+ just for healthcare in retirement.
              </ThreatText>
            </ThreatCard>
            
            <ThreatCard>
              <ThreatIcon>🏠</ThreatIcon>
              <ThreatTitle>Rising Living Expenses</ThreatTitle>
              <ThreatText>
                Housing, food, and utilities keep climbing. Your fixed income stays the same 
                while everything else gets more expensive.
              </ThreatText>
            </ThreatCard>
            
            <ThreatCard>
              <ThreatIcon></ThreatIcon>
              <ThreatTitle>Longevity Risk</ThreatTitle>
              <ThreatText>
                Living 20-30 years in retirement means decades of inflation impact. 
                Will your savings last as long as you do?
              </ThreatText>
            </ThreatCard>
          </ThreatGrid>
        </Container>
      </Section>

      <Section bg="#f8f9fa">
        <Container>
          <SectionTitle>How We Protect Your Retirement</SectionTitle>
          <SectionSubtitle>
            Our proven strategies help shield your savings from inflation's devastating effects
          </SectionSubtitle>
          
          <SolutionGrid>
            <SolutionCard>
              <SolutionIcon></SolutionIcon>
              <SolutionTitle>Inflation-Protected Annuities</SolutionTitle>
              <SolutionText>
                We identify annuities with built-in inflation protection features that 
                adjust your income as costs rise.
              </SolutionText>
            </SolutionCard>
            
            <SolutionCard>
              <SolutionIcon></SolutionIcon>
              <SolutionTitle>Strategic Diversification</SolutionTitle>
              <SolutionText>
                Balance growth potential with stability using a mix of products 
                designed to outpace inflation over time.
              </SolutionText>
            </SolutionCard>
            
            <SolutionCard>
              <SolutionIcon></SolutionIcon>
              <SolutionTitle>Personalized Analysis</SolutionTitle>
              <SolutionText>
                Every retirement is unique. We analyze your specific situation and 
                create a custom inflation defense strategy.
              </SolutionText>
            </SolutionCard>
          </SolutionGrid>
        </Container>
      </Section>

      <TestimonialSection>
        <Container>
          <SectionTitle>Real People, Real Protection</SectionTitle>
          <SectionSubtitle>
            See how we've helped others safeguard their retirement from inflation
          </SectionSubtitle>
          
          <TestimonialGrid>
            <TestimonialCard>
              <Stars>★★★★★</Stars>
              <TestimonialText>
                "I was worried sick about inflation eating away at my savings. Cap showed me 
                how to protect my money and now I sleep well at night."
              </TestimonialText>
              <TestimonialAuthor>Steve L.</TestimonialAuthor>
            </TestimonialCard>
            
            <TestimonialCard>
              <Stars>★★★★★</Stars>
              <TestimonialText>
                "They found me an annuity that actually keeps up with inflation. I didn't even 
                know these products existed until I talked to them."
              </TestimonialText>
              <TestimonialAuthor>Mark K.</TestimonialAuthor>
            </TestimonialCard>
            
            <TestimonialCard>
              <Stars>★★★★★</Stars>
              <TestimonialText>
                "One of the better decisions I've made at this point in my life. My retirement 
                income is now protected from inflation."
              </TestimonialText>
              <TestimonialAuthor>Clement D.</TestimonialAuthor>
            </TestimonialCard>
          </TestimonialGrid>
        </Container>
      </TestimonialSection>

      <ProcessSection>
        <Container>
          <SectionTitle>Your Path to Inflation Protection</SectionTitle>
          <SectionSubtitle>
            Getting started is easy and completely free
          </SectionSubtitle>
          
          <ProcessSteps>
            <ProcessStep>
              <StepNumber>1</StepNumber>
              <StepTitle>Free Consultation</StepTitle>
              <StepText>
                30-minute call to understand your retirement goals and inflation concerns
              </StepText>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>2</StepNumber>
              <StepTitle>Custom Analysis</StepTitle>
              <StepText>
                We analyze your current position and create a personalized protection strategy
              </StepText>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>3</StepNumber>
              <StepTitle>Product Comparison</StepTitle>
              <StepText>
                Using our AI tools, we compare products from multiple carriers to find your best fit
              </StepText>
            </ProcessStep>
            
            <ProcessStep>
              <StepNumber>4</StepNumber>
              <StepTitle>Ongoing Support</StepTitle>
              <StepText>
                We're here for you throughout retirement, adjusting strategies as needed
              </StepText>
            </ProcessStep>
          </ProcessSteps>
        </Container>
      </ProcessSection>

      <CTASection>
        <Container>
          <CTATitle>Every Day You Wait Costs You Money</CTATitle>
          <CTAText>
            Protect your retirement from inflation today with expert guidance
          </CTAText>
          <CTAButton onClick={openCalendly}>
            SCHEDULE YOUR FREE INFLATION PROTECTION CONSULTATION
          </CTAButton>
          <div style={{ marginTop: '20px', color: 'rgba(255, 255, 255, 0.8)' }}>
            Or call us directly: <strong style={{ color: '#ffd700' }}>800-922-1894</strong>
          </div>
        </Container>
      </CTASection>
    </SharedLayout>
  );
};